import { z } from "zod";
import { getIDToken } from "utils/misc";
import { QuestionSchema } from "./types/common";
import { BookSchema, QuestionGroupSchema } from "./types/persisted";

type Question = z.infer<typeof QuestionSchema>;
type Book = z.infer<typeof BookSchema>;
type QuestionGroup = z.infer<typeof QuestionGroupSchema>;

const BASE_URL = `${process.env.REACT_APP_API_URL_V2}/v2/support/health-forms`;

const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  const token = getIDToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const QuestionListItemSchema = QuestionSchema.pick({
  id: true,
  question: true,
  responseType: true,
  semanticKey: true,
});

const BookListItemSchema = BookSchema.pick({
  id: true,
  name: true,
}).extend({
  _type: z.literal("book"),
});

const QuestionGroupListItemSchema = QuestionGroupSchema.pick({
  id: true,
  name: true,
}).extend({
  _type: z.literal("question_group"),
});

type QuestionListItem = z.infer<typeof QuestionListItemSchema>;
type BookListItem = z.infer<typeof BookListItemSchema>;
type QuestionGroupListItem = z.infer<typeof QuestionGroupListItemSchema>;

export const healthFormsApi = {
  questions: {
    list: async (): Promise<QuestionListItem[]> => {
      const res = await fetch(`${BASE_URL}/questions`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch questions");
      const data = await res.json();
      return z.array(QuestionListItemSchema).parse(data);
    },

    get: async (id: number) => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question ${id}`);
      const data = await res.json();
      return QuestionSchema.parse(data);
    },

    create: async (question: Omit<Question, "id" | "hash" | "updatedAt">): Promise<Question> => {
      const res = await fetch(`${BASE_URL}/questions`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(question),
      });
      if (!res.ok) throw new Error("Failed to create question");
      return res.json();
    },

    update: async (id: number, question: Question): Promise<Question> => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(question),
      });
      if (!res.ok) throw new Error(`Failed to update question ${id}`);
      if (res.status === 204) return question;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete question ${id}`);
    },
  },

  books: {
    list: async (): Promise<BookListItem[]> => {
      const res = await fetch(`${BASE_URL}/books`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch books");
      const data = await res.json();
      return z.array(BookListItemSchema).parse(data);
    },

    get: async (id: number) => {
      const res = await fetch(`${BASE_URL}/books/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch book ${id}`);
      const data = await res.json();
      return BookSchema.parse(data);
    },

    create: async (book: Omit<Book, "id" | "updatedAt">): Promise<Book> => {
      const res = await fetch(`${BASE_URL}/books`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(book),
      });
      if (!res.ok) throw new Error("Failed to create book");
      return res.json();
    },

    update: async (id: number, book: Book): Promise<Book> => {
      const res = await fetch(`${BASE_URL}/books/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(book),
      });
      if (!res.ok) throw new Error(`Failed to update book ${id}`);
      if (res.status === 204) return book;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/books/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete book ${id}`);
    },
  },

  questionGroups: {
    list: async (): Promise<QuestionGroupListItem[]> => {
      const res = await fetch(`${BASE_URL}/question-groups`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch question groups");
      const data = await res.json();
      return z.array(QuestionGroupListItemSchema).parse(data);
    },

    get: async (id: number) => {
      const res = await fetch(`${BASE_URL}/question-groups/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question group ${id}`);
      const data = await res.json();
      return QuestionGroupSchema.parse(data);
    },

    create: async (group: Omit<QuestionGroup, "id" | "updatedAt">): Promise<QuestionGroup> => {
      const res = await fetch(`${BASE_URL}/question-groups`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(group),
      });
      if (!res.ok) throw new Error("Failed to create question group");
      return res.json();
    },

    update: async (id: number, group: QuestionGroup): Promise<QuestionGroup> => {
      const res = await fetch(`${BASE_URL}/question-groups/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(group),
      });
      if (!res.ok) throw new Error(`Failed to update question group ${id}`);
      if (res.status === 204) return group;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/question-groups/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete question group ${id}`);
    },
  },
};

export type { QuestionListItem, BookListItem, QuestionGroupListItem };
